import Cookies from 'js-cookie'
import Vue from 'vue'
import VueI18n from 'vue-i18n'

// Locale JSONs live in the Vue 3 tree (shell inversion, phase 2.4) — both
// bundles load the same files.
import en from '../../../zendrop-vue3/src/locales/en.json'
import { formatInstant, toInstant } from '~/services/date-format-service'
import util from '~/util'

const localeLoaders = import.meta.glob('../../../zendrop-vue3/src/locales/*.json')
const supportedLocales = Object.keys(localeLoaders).map((path) => path.match(/([\w-]+)\.json$/)[1])

function publishLocaleTerms(messages) {
  window.config = window.config || {}
  window.config.locales_terms = messages
}

export default {
  _instance: null,
  messages: { en },
  loadLocale(locale) {
    if (this.messages[locale]) {
      return Promise.resolve()
    }
    const loader = localeLoaders[`../../../zendrop-vue3/src/locales/${locale}.json`]
    if (!loader) {
      return Promise.resolve()
    }
    return loader().then((module) => {
      this.messages[locale] = module.default ?? module
      publishLocaleTerms(this.messages)
      if (this._instance) {
        this._instance.setLocaleMessage(locale, this.messages[locale])
      }
    })
  },
  setLocale(locale) {
    if (!supportedLocales.includes(locale)) {
      return Promise.resolve()
    }
    const shared = window.config?.locales_terms?.[locale]
    if (!this.messages[locale] && shared) {
      this.messages[locale] = shared
      this.getInstance().setLocaleMessage(locale, shared)
    }
    if (this.messages[locale]) {
      this.getInstance().locale = locale
      return Promise.resolve()
    }
    return this.loadLocale(locale).then(() => {
      this.getInstance().locale = locale
    })
  },
  install() {
    Vue.use(VueI18n)
    Vue.component('i18n').options.props = {
      ...Vue.component('i18n').options.props,
      displayHtmlTags: {
        type: Boolean,
        required: false,
        default: false,
      },
    }
    Vue.component('i18n').options.render = function (h, ref) {
      var data = ref.data
      var parent = ref.parent
      var props = ref.props
      var slots = ref.slots

      var $i18n = parent.$i18n
      if (!$i18n) {
        return
      }

      var path = props.path
      var locale = props.locale
      var params = slots()
      var children = $i18n.i(path, locale, params)

      var tag = (!!props.tag && props.tag !== true) || props.tag === false ? props.tag : 'span'

      if (props.displayHtmlTags) {
        let fn = (item) => (typeof item === 'string' ? h('span', { domProps: { innerHTML: item } }) : item)

        children = Array.isArray(children) ? children.map(fn) : [children].map(fn)
      }

      return tag ? h(tag, data, children) : children
    }

    Vue.prototype.$dt = Vue.prototype.$d = (dateStr, format = null, locale = null) => {
      let date = toInstant(dateStr)
      return date ? this.getInstance().d(date, format, locale) : ''
    }

    Vue.prototype.$formatDate = (value, preset = 'date', locale = null) =>
      formatInstant(value, preset, locale ?? this.getInstance().locale)
  },
  getInstance() {
    if (!this._instance) {
      let fallbackLocale = 'en'
      let locale = Cookies.get('zendrop-lang') || document.documentElement.lang || fallbackLocale

      // Accept only presented locales
      if (!supportedLocales.includes(locale)) {
        locale = fallbackLocale
      }

      const numberFormats = {
        en: {
          currency: {
            style: 'currency',
            currency: 'USD',
          },
        },
      }

      const dateTimeFormats = {
        en: {
          monthCustom: {
            month: 'long',

            //TODO: improve
            day: 'numeric',
          },
        },
      }

      Cookies.set('zendrop-lang', locale, {
        expires: 365,
        domain: util.getCookieDomain(),
      })

      this._instance = new VueI18n({
        locale: locale,
        fallbackLocale: fallbackLocale,
        messages: this.messages,
        numberFormats: numberFormats,
        dateTimeFormats: dateTimeFormats,
      })

      publishLocaleTerms(this.messages)
      this.loadLocale(locale)
    }

    return this._instance
  },
}
